import type { PlanFormState } from './types';
import type { Instant, Period } from '@js-joda/core';
import type {
    ReplaceSubscriptionPlanRequest,
    Subscription,
    SubscriptionPlan,
    SubscriptionPlanWriteRequest,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { BadgeFill } from 'src/components/ui/badges/badge';
import { getTerminalPlanPriceStage } from 'src/lib/subscription/planPresentation';

type BadgeColor = 'green' | 'yellow' | 'red' | 'gray' | 'cyan';

export interface StatusBadgeProps {
    color: BadgeColor;
    fill: BadgeFill;
    label: string;
}

export const statusBadgeProps: Record<Subscription['status'], StatusBadgeProps> = {
    active: { color: 'green', fill: 'medium', label: 'Active' },
    trialing: { color: 'cyan', fill: 'medium', label: 'Trialing' },
    active_canceling: { color: 'yellow', fill: 'medium', label: 'Canceling' },
    active_plan_change_scheduled: { color: 'yellow', fill: 'medium', label: 'Plan Change Scheduled' },
    past_due: { color: 'red', fill: 'light', label: 'Past Due' },
    pending_payment_method: { color: 'yellow', fill: 'light', label: 'Pending Payment' },
    pending_activation: { color: 'yellow', fill: 'light', label: 'Pending Activation' },
    canceled: { color: 'red', fill: 'medium', label: 'Canceled' },
    aborted: { color: 'red', fill: 'medium', label: 'Aborted' },
    paused: { color: 'gray', fill: 'medium', label: 'Paused' },
};

export function formatDate(instant: Instant): string {
    try {
        return new Date(instant.toString()).toLocaleDateString();
    } catch {
        return instant.toString();
    }
}

export function formatPeriod(period: Period | null): string {
    if (period === null) return 'One-time';

    const years = period.years();
    const months = period.months();
    const days = period.days();

    const parts: string[] = [];
    if (years === 1) parts.push('Year');
    else if (years > 1) parts.push(`${years} Years`);
    if (months === 1) parts.push('Month');
    else if (months > 1) parts.push(`${months} Months`);
    if (days === 1) parts.push('Day');
    else if (days > 1) parts.push(`${days} Days`);

    return parts.length > 0 ? `Per ${parts.join(', ')}` : period.toString();
}

export function formatPrice(price: number, currency: string): string {
    if (price === 0) return 'Free';
    return `${price} ${currency}`;
}

export function planToFormState(plan: SubscriptionPlan): PlanFormState {
    const terminalStage = getTerminalPlanPriceStage(plan.pricing);
    if (!terminalStage) throw new Error(`Subscription plan ${plan.id} has no pricing stages`);

    return {
        name: plan.name,
        description: plan.description ?? '',
        price: String(terminalStage.price),
        currency: plan.currency,
        billingInterval: terminalStage.billingInterval?.toString() ?? '',
        isForProfessionalTraders: plan.isForProfessionalTraders,
        isForNonProfessionalTraders: plan.isForNonProfessionalTraders,
        active: plan.active,
        selfServe: plan.selfServe,
        isAddOn: plan.isAddOn,
        inviteCodeRequired: plan.inviteCodeRequired,
        advertised: plan.advertised,
        maxPerUser: plan.maxPerUser !== null ? String(plan.maxPerUser) : '',
        features: plan.features ?? [],
        roles: (plan.roles ?? []).filter((r): r is string => typeof r === 'string'),
        groups: plan.groups.map((g) => ({ groupId: g.groupId, permission: g.permission })),
        badgeText: plan.badgeText ?? '',
        default: plan.default,
        existingSubscriptionBehavior: 'none',
    };
}

export function createDefaultFormState(): PlanFormState {
    return {
        name: '',
        description: '',
        price: '',
        currency: 'USD',
        billingInterval: 'P1M',
        isForProfessionalTraders: true,
        isForNonProfessionalTraders: true,
        active: false,
        selfServe: false,
        isAddOn: false,
        inviteCodeRequired: false,
        advertised: false,
        maxPerUser: '',
        features: [],
        roles: [],
        groups: [],
        badgeText: '',
        default: false,
        existingSubscriptionBehavior: 'none',
    };
}

/**
 * Build the full plan body the backend PUT/POST endpoints expect.
 *
 * The backend write contract is `.strict()` and requires every plan field. The form edits
 * the terminal pricing stage and preserves other stages from the original plan.
 */
export function formStateToWriteRequest(
    form: PlanFormState,
    original?: SubscriptionPlan,
): SubscriptionPlanWriteRequest {
    const formPrice = form.price === '' ? 0 : Number(form.price);
    const formBillingInterval = form.billingInterval === '' ? null : form.billingInterval;
    const formMaxPerUser = form.maxPerUser === '' ? null : Number(form.maxPerUser);
    const formFeatures = form.features.map((f) => f.trim()).filter(Boolean);
    const ongoingStageIndex = original?.pricing.findIndex((stage) => stage.period === null) ?? -1;
    const terminalStageIndex = original
        ? ongoingStageIndex === -1
            ? original.pricing.length - 1
            : ongoingStageIndex
        : 0;
    const pricing: SubscriptionPlanWriteRequest['pricing'] = original
        ? original.pricing.map((stage, index) => ({
              name: stage.name,
              price: index === terminalStageIndex ? formPrice : stage.price,
              billingInterval:
                  index === terminalStageIndex ? formBillingInterval : (stage.billingInterval?.toString() ?? null),
              period: stage.period?.toString() ?? null,
          }))
        : [{ price: formPrice, billingInterval: formBillingInterval, period: null }];

    return {
        name: form.name,
        description: form.description || null,
        currency: form.currency,
        pricing,
        isForProfessionalTraders: form.isForProfessionalTraders,
        isForNonProfessionalTraders: form.isForNonProfessionalTraders,
        active: form.active,
        selfServe: form.selfServe,
        isAddOn: form.isAddOn,
        inviteCodeRequired: form.inviteCodeRequired,
        advertised: form.advertised,
        maxPerUser: formMaxPerUser,
        features: formFeatures.length > 0 ? formFeatures : null,
        roles: form.roles,
        groups: form.groups,
        badgeText: form.badgeText || null,
        default: form.default,
        // Fields not exposed by the form — preserve from the original on edit; default on create.
        includeFeaturesFromPlans: original?.includeFeaturesFromPlans ?? [],
        replacesPlanIds: original?.replacesPlanIds ?? [],
    };
}

export function formStateToReplaceRequest(
    form: PlanFormState,
    original: SubscriptionPlan,
): ReplaceSubscriptionPlanRequest {
    return {
        ...formStateToWriteRequest(form, original),
        existingSubscriptionBehavior: form.existingSubscriptionBehavior,
    };
}
